import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { invoicePaidMap, type PegasusPayment } from "@/features/payments/payments";

// ---------- Types matching what we mirror from ESC ----------

export interface CustomerRow {
  custno: string;
  fullname: string | null;
  firstname: string | null;
  lastname: string | null;
  phone1: string | null;
  phone2: string | null;
  phone3: string | null;
  phone4: string | null;
  lbl_phone1: string | null;
  lbl_phone2: string | null;
  lbl_phone3: string | null;
  lbl_phone4: string | null;
  /** ESC bill-to address (Customer.Add1..Zip) — the Billing view; differs
   *  from the service address on rentals/property-manager accounts. */
  add1: string | null;
  add2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  customerinactive: boolean;
  /** DNS-01: ESC credit rating (mirror; BAD-style values = Do Not Service). */
  credit_rating: string | null;
}

export interface LocationRow {
  /** PROP-01: true = Pegasus-added property (movable); absent = ESC mirror row. */
  peg?: boolean;
  custno: string;
  locno: string;
  locname: string | null;
  add1: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone1: string | null;
  phone2: string | null;
  phone3: string | null;
  phone4: string | null;
  phone5: string | null;
  phone6: string | null;
  email: string | null;
  email2: string | null;
  email3: string | null;
  email4: string | null;
  email5: string | null;
  email6: string | null;
  /** ESC Address-Information contact grid (MIR-LOCLBL): six named slots —
   *  "Cell - Truc | 612-226-8099 | tttruong67@…". Null until the loccontacts
   *  backfill runs; the header falls back to bare lines when absent. */
  contact1: string | null;
  contact2: string | null;
  contact3: string | null;
  contact4: string | null;
  contact5: string | null;
  contact6: string | null;
  ext1: string | null;
  ext2: string | null;
  ext3: string | null;
  ext4: string | null;
  ext5: string | null;
  ext6: string | null;
  jobtitle1: string | null;
  jobtitle2: string | null;
  jobtitle3: string | null;
  jobtitle4: string | null;
  jobtitle5: string | null;
  jobtitle6: string | null;
  emailtasks1: number | null;
  emailtasks2: number | null;
  zone: string | null;
  receives_notifications: boolean | null;
  disable_emails: boolean | null;
  location_inactive: boolean | null;
  notes: string | null;
  /** HDR-DISPATCH-01: ESC's location class (custom_1) — PLUS 1 / PLUS 2 /
   *  PLUS 3 / CUSTOMER / COMMERCIAL / COMM PLUS. Mirror rows carry it
   *  (select *); Pegasus-added properties don't (absent). */
  custom_1?: string | null;
}

export interface CustomerWithLocations extends CustomerRow {
  locations: LocationRow[];
  /** True for a Pegasus-born customer (no ESC mirror row). */
  native?: boolean;
  /** RECORD-READ-MODEL (34i97): ESC's name as ESC has it — `fullname` is the name SHOWN
   *  (the office correction when there is one), so every reader agrees. */
  esc_fullname?: string | null;
  /** Residential | Commercial as a Pegasus fact; null = read ESC's custom_1. */
  customer_type?: "residential" | "commercial" | null;
}

export interface CustomerDispatchRow {
  dispatch: string;
  tech_empno: string;
  appointment_date: string;
  tpromtime: string | null;
  window_code: string | null;
  status: string | null;
  notes: string | null;
  custno: string;
  locno: string | null;
  locname: string | null;
  service_address: string | null;
  city: string | null;
  tech_name: string | null;
  tech_first: string | null;
  tech_last: string | null;
  /** True = Pegasus-native visit (no ESC dispatch to peek). */
  peg?: boolean;
}

// ---------- Search ----------

/**
 * SEARCH-01 (2026-09-17, Seth: "20 2nd st ne" should find Laura Wade — the
 * results were "all over the place"). Search lives in Postgres now:
 * `pegasus_search_customers(q)` reads `pegasus_search_index` (one row per
 * customer-location, name / address / phone normalized the same way the query
 * is — Street→St, Northeast→NE, Second→2nd, "Wade,Laura"→"wade laura"), every
 * word must match (prefix on words, exact on finished numbers, direction and
 * street-type words only rank), phone-shaped input matches phone digits, digits
 * match an account number, a misspelled name falls back to trigram similarity.
 * Exact house-number + street matches rank first, then the most recently seen
 * account. One round trip, hydrated: the account once, its best-matching
 * location first. The index is kept fresh by triggers on the mirror + Pegasus
 * customer tables, so a brand-new Pegasus customer is searchable at once.
 *
 * INACTIVE-OVERLAY (Seth 2026-08-13): search HIDES inactive accounts by
 * default — both ESC's own customerinactive flag and the Pegasus-native
 * pegasus_customer_status overlay. Pass { includeInactive: true } (the search
 * page's checkbox) to see them; direct /customers/:custno navigation always
 * works, so history is never unreachable.
 */
interface SearchHit {
  custno: string;
  locno: string | null;
  source: "esc" | "pegasus";
  rank: number;
  customer: Record<string, unknown> | null;
  locations: LocationRow[] | null;
  override: CustomerOverride | null;
}

export function useCustomerSearch(query: string, opts?: { includeInactive?: boolean }) {
  const includeInactive = opts?.includeInactive ?? false;
  return useQuery({
    queryKey: ["customer_search", query, includeInactive],
    queryFn: async (): Promise<CustomerWithLocations[]> => {
      const q = query.trim();
      if (q.length === 0) return [];
      // 360-AUDIT wave 2 (G): a dropped error here silently removed customers
      // from search — "not in the system" would be quoted as fact and a
      // duplicate created. Throw; the bar shows an error.
      const { data, error } = await supabase.rpc("pegasus_search_customers", {
        p_q: q,
        p_include_inactive: includeInactive,
        p_limit: 50,
      });
      if (error) throw error;
      const out: CustomerWithLocations[] = [];
      for (const h of (data ?? []) as SearchHit[]) {
        if (!h.customer) continue;
        if (h.source === "pegasus") {
          out.push(pegasusToCustomer(h.customer as unknown as PegasusCustomerRow));
          continue;
        }
        const c = h.customer as unknown as CustomerRow;
        const ovr = h.override;
        out.push({
          ...c,
          // EDIT-360: the corrected name IS the name, everywhere it renders.
          fullname: ovr?.display_name?.trim() ? ovr.display_name : c.fullname,
          locations: (h.locations ?? []) as LocationRow[],
        });
      }
      return out;
    },
    staleTime: 30 * 1000,
    enabled: query.trim().length > 0,
  });
}

// ---------- EDIT-360: identity override (ESC-mirrored) + native edit ----------
export interface CustomerOverride {
  custno: string;
  display_name: string | null;
  phone: string | null;
  phone_label: string | null;
  phone2: string | null;
  phone2_label: string | null;
  email: string | null;
  note: string | null;
}
export function useCustomerOverride(custno: string | null | undefined) {
  return useQuery({
    queryKey: ["customer_override", custno],
    enabled: Boolean(custno),
    staleTime: 30 * 1000,
    queryFn: async (): Promise<CustomerOverride | null> => {
      const { data } = await supabase.from("pegasus_customer_override").select("*").eq("custno", custno as string).maybeSingle();
      return (data ?? null) as CustomerOverride | null;
    },
  });
}
export function useSaveCustomerOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: CustomerOverride) => {
      const { data: userData } = await supabase.auth.getUser();
      const empty = !v.display_name && !v.phone && !v.phone2 && !v.email && !v.note;
      if (empty) {
        const { error } = await supabase.from("pegasus_customer_override").delete().eq("custno", v.custno);
        if (error) throw error;
        return;
      }
      const { error } = await supabase.from("pegasus_customer_override").upsert(
        { ...v, updated_at: new Date().toISOString(), updated_by: userData?.user?.email ?? null },
        { onConflict: "custno" },
      );
      if (error) throw error;
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["customer_override", v.custno] });
      qc.invalidateQueries({ queryKey: ["customer", v.custno] }); // the record carries the shown name
      qc.invalidateQueries({ queryKey: ["customer_search"] });
      qc.invalidateQueries({ queryKey: ["customer", v.custno] });
    },
  });
}
/** Is this a Pegasus-NATIVE customer (born in Pegasus — directly editable)? */
export function useIsPegasusNative(custno: string | null | undefined) {
  return useQuery({
    queryKey: ["is_pegasus_native", custno],
    enabled: Boolean(custno),
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<boolean> => {
      const { data } = await supabase.from("pegasus_customer").select("custno").eq("custno", custno as string).maybeSingle();
      return Boolean(data);
    },
  });
}
export function useSavePegasusCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { custno: string; fullname: string; phone: string | null; email: string | null; add1: string | null; city: string | null; state: string | null; zip: string | null }) => {
      const { custno, ...patch } = v;
      const { error } = await supabase.from("pegasus_customer")
        .update({ ...patch, updated_at: new Date().toISOString() })
        .eq("custno", custno);
      if (error) throw error;
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["customer", v.custno] });
      qc.invalidateQueries({ queryKey: ["customer_search"] });
    },
  });
}

// ---------- MERGE-360: duplicate-account merge (Pegasus-native data) ----------
export interface MergeInfo {
  mergedInto: { custno: string; reason: string | null; merged_at: string } | null;
  duplicates: { custno: string; reason: string | null; merged_at: string }[];
}
export function useCustomerMergeInfo(custno: string | null | undefined) {
  return useQuery({
    queryKey: ["customer_merge_info", custno],
    enabled: Boolean(custno),
    staleTime: 30 * 1000,
    queryFn: async (): Promise<MergeInfo> => {
      const [asDup, asPrimary] = await Promise.all([
        supabase.from("pegasus_customer_merge").select("primary_custno, reason, merged_at").eq("duplicate_custno", custno as string).is("unmerged_at", null).maybeSingle(),
        supabase.from("pegasus_customer_merge").select("duplicate_custno, reason, merged_at").eq("primary_custno", custno as string).is("unmerged_at", null),
      ]);
      return {
        mergedInto: asDup.data ? { custno: asDup.data.primary_custno as string, reason: (asDup.data.reason as string) ?? null, merged_at: asDup.data.merged_at as string } : null,
        duplicates: ((asPrimary.data ?? []) as { duplicate_custno: string; reason: string | null; merged_at: string }[]).map((r) => ({ custno: r.duplicate_custno, reason: r.reason, merged_at: r.merged_at })),
      };
    },
  });
}
export function useMergePreview(custno: string | null) {
  return useQuery({
    queryKey: ["merge_preview", custno],
    enabled: Boolean(custno),
    staleTime: 10 * 1000,
    queryFn: async (): Promise<Record<string, number>> => {
      const { data, error } = await supabase.rpc("merge_preview", { p_custno: custno });
      if (error) throw error;
      return (data ?? {}) as Record<string, number>;
    },
  });
}
export function useMergeCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { duplicate: string; primary: string; reason: string }) => {
      const { data: userData } = await supabase.auth.getUser();
      const { data, error } = await supabase.rpc("merge_pegasus_customer", {
        p_duplicate: v.duplicate, p_primary: v.primary, p_reason: v.reason,
        p_actor: userData?.user?.email ?? null,
      });
      if (error) throw error;
      const res = data as { ok?: boolean; error?: string };
      if (!res?.ok) throw new Error(res?.error ?? "merge failed");
      return res;
    },
    onSuccess: () => qc.invalidateQueries(),
  });
}
export function useUnmergeCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (duplicate: string) => {
      const { data: userData } = await supabase.auth.getUser();
      const { data, error } = await supabase.rpc("unmerge_pegasus_customer", {
        p_duplicate: duplicate, p_actor: userData?.user?.email ?? null,
      });
      if (error) throw error;
      const res = data as { ok?: boolean; error?: string };
      if (!res?.ok) throw new Error(res?.error ?? "unmerge failed");
      return res;
    },
    onSuccess: () => qc.invalidateQueries(),
  });
}

// ---------- INACTIVE-OVERLAY: the 360's flag + toggle ----------
export interface CustomerStatusFlag {
  custno: string;
  inactive: boolean;
  reason: string | null;
  /** DNS-01: the Pegasus-native Do Not Service mark. */
  do_not_service: boolean;
  dns_reason: string | null;
  updated_at: string;
  updated_by: string | null;
}

/** DNS-01 — ONE rule for "is this account Do Not Service": the Pegasus mark,
 *  or an ESC credit rating that reads as one (BAD / DNS / DO NOT / NO SERVICE). */
export function isDoNotService(
  flag: Pick<CustomerStatusFlag, "do_not_service"> | null | undefined,
  creditRating: string | null | undefined,
): boolean {
  if (flag?.do_not_service === true) return true;
  return /\b(bad|dns|do\s*not|no\s*serv)/i.test(creditRating ?? "");
}
export function useCustomerStatusFlag(custno: string | null | undefined) {
  return useQuery({
    queryKey: ["customer_status_flag", custno],
    enabled: Boolean(custno),
    staleTime: 30 * 1000,
    queryFn: async (): Promise<CustomerStatusFlag | null> => {
      const { data } = await supabase
        .from("pegasus_customer_status")
        .select("*")
        .eq("custno", custno as string)
        .maybeSingle();
      return (data ?? null) as CustomerStatusFlag | null;
    },
  });
}
export function useSetCustomerInactive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { custno: string; inactive: boolean; reason?: string | null }) => {
      const { data: userData } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_customer_status").upsert(
        {
          custno: v.custno,
          inactive: v.inactive,
          reason: v.reason ?? null,
          updated_at: new Date().toISOString(),
          updated_by: userData?.user?.email ?? null,
        },
        { onConflict: "custno" },
      );
      if (error) throw error;
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["customer_status_flag", v.custno] });
      qc.invalidateQueries({ queryKey: ["customer_search"] });
    },
  });
}

/** DNS-01: set / clear the Do Not Service mark (same status row as the
 *  inactive overlay; partial upsert leaves the other flags alone). */
export function useSetDoNotService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { custno: string; on: boolean; reason?: string | null }) => {
      const { data: userData } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_customer_status").upsert(
        {
          custno: v.custno,
          do_not_service: v.on,
          dns_reason: v.on ? (v.reason ?? null) : null,
          updated_at: new Date().toISOString(),
          updated_by: userData?.user?.email ?? null,
        },
        { onConflict: "custno" },
      );
      if (error) throw error;
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["customer_status_flag", v.custno] });
    },
  });
}

// ---------- Single customer detail ----------

/**
 * Get a batch of customers with their locations - used by the Recents panel.
 */
export function useCustomersByIds(custNos: string[]) {
  const sortedKey = [...custNos].sort().join(",");
  return useQuery({
    queryKey: ["customers_by_ids", sortedKey],
    queryFn: async (): Promise<CustomerWithLocations[]> => {
      if (custNos.length === 0) return [];
      const [{ data: customers, error: cErr }, { data: locs, error: lErr }] =
        await Promise.all([
          supabase.from("mirror_customer").select("*").in("custno", custNos),
          supabase.from("mirror_location").select("*").in("custno", custNos),
        ]);
      if (cErr) throw cErr;
      if (lErr) throw lErr;
      const byCustno = new Map<string, LocationRow[]>();
      for (const loc of (locs ?? []) as LocationRow[]) {
        if (!byCustno.has(loc.custno)) byCustno.set(loc.custno, []);
        byCustno.get(loc.custno)!.push(loc);
      }
      const result = ((customers ?? []) as CustomerRow[]).map((c) => ({
        ...c,
        locations: byCustno.get(c.custno) ?? [],
      }));
      // Any ids not in the ESC mirror are Pegasus-native (NEWCUST) — fetch them.
      const foundIds = new Set(result.map((c) => c.custno));
      const missing = custNos.filter((c) => !foundIds.has(c));
      if (missing.length) {
        const { data: pcs } = await supabase
          .from("pegasus_customer").select("*").in("custno", missing);
        for (const pc of (pcs ?? []) as PegasusCustomerRow[]) result.push(pegasusToCustomer(pc));
      }
      // Preserve the input order (most-recent first)
      const indexByCustno = new Map(custNos.map((c, i) => [c, i]));
      result.sort(
        (a, b) =>
          (indexByCustno.get(a.custno) ?? 999) -
          (indexByCustno.get(b.custno) ?? 999),
      );
      return result;
    },
    enabled: custNos.length > 0,
    staleTime: 60 * 1000,
  });
}

/**
 * Get one customer with all their locations - two queries, combined.
 */
/** A brand-new customer born in Pegasus (NEWCUST, Seth 2026-06-26) — not in the
 *  read-only ESC mirror. custno is numeric from 3000001. */
export interface PegasusCustomerRow {
  custno: string;
  firstname: string | null;
  lastname: string | null;
  fullname: string;
  phone: string | null;
  email: string | null;
  add1: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  source: string | null;
  lead_conversation_key: string | null;
  active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Map a Pegasus-native customer into the 360's CustomerWithLocations shape —
 *  its address IS its single service location (it has no ESC mirror_location
 *  rows). Everything ESC-historical (invoices, agreements) is naturally empty. */
export function pegasusToCustomer(p: PegasusCustomerRow): CustomerWithLocations {
  return {
    native: true,
    esc_fullname: null,
    customer_type: (p as PegasusCustomerRow & { customer_type?: "residential" | "commercial" | null }).customer_type ?? null,
    custno: p.custno,
    fullname: p.fullname,
    firstname: p.firstname,
    lastname: p.lastname,
    phone1: p.phone, phone2: null, phone3: null, phone4: null,
    lbl_phone1: p.phone ? "Cell" : null, lbl_phone2: null, lbl_phone3: null, lbl_phone4: null,
    add1: p.add1, add2: null, city: p.city, state: p.state, zip: p.zip,
    customerinactive: !p.active,
    credit_rating: null,
    locations: [
      {
        custno: p.custno, locno: "00001", locname: p.fullname,
        add1: p.add1, city: p.city, state: p.state, zip: p.zip,
        phone1: p.phone, phone2: null, phone3: null, phone4: null, phone5: null, phone6: null,
        email: p.email, email2: null, email3: null, email4: null, email5: null, email6: null,
        contact1: null, contact2: null, contact3: null, contact4: null, contact5: null, contact6: null,
        ext1: null, ext2: null, ext3: null, ext4: null, ext5: null, ext6: null,
        jobtitle1: null, jobtitle2: null, jobtitle3: null, jobtitle4: null, jobtitle5: null, jobtitle6: null,
        emailtasks1: null, emailtasks2: null, zone: null,
        receives_notifications: null, disable_emails: null, location_inactive: !p.active, notes: null,
      },
    ],
  };
}

/** PROP-01: a pegasus_location row dressed as a LocationRow — the whole 360
 *  (PropertiesBar, dispatch creation, scoping) treats it like any property. */
interface PegLocationRow {
  id: string; custno: string; locno: string; locname: string | null; add1: string | null;
  city: string | null; state: string | null; zip: string | null; notes: string | null;
  active: boolean; moved_from_custno: string | null;
}
function pegLocToRow(r: PegLocationRow): LocationRow {
  return {
    peg: true,
    custno: r.custno, locno: r.locno, locname: r.locname,
    add1: r.add1, city: r.city, state: r.state, zip: r.zip,
    phone1: null, phone2: null, phone3: null, phone4: null, phone5: null, phone6: null,
    email: null, email2: null, email3: null, email4: null, email5: null, email6: null,
    contact1: null, contact2: null, contact3: null, contact4: null, contact5: null, contact6: null,
    ext1: null, ext2: null, ext3: null, ext4: null, ext5: null, ext6: null,
    jobtitle1: null, jobtitle2: null, jobtitle3: null, jobtitle4: null, jobtitle5: null, jobtitle6: null,
    emailtasks1: null, emailtasks2: null, zone: null,
    receives_notifications: null, disable_emails: null, location_inactive: !r.active, notes: r.notes,
  };
}

/** Add a property under ANY customer (ESC or Pegasus-native). ESC's own
 *  locations stay read-only — this is the Pegasus overlay (PROP-01). */
export function useAddProperty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { custno: string; locname: string; add1: string; city: string; state: string; zip: string; notes?: string }) => {
      const who = (await supabase.auth.getUser()).data.user?.email ?? null;
      // Next P-number for this customer (P0001, P0002 — never collides with ESC's numerics).
      const { data } = await supabase
        .from("pegasus_location")
        .select("locno")
        .eq("custno", v.custno)
        .like("locno", "P%")
        .order("locno", { ascending: false })
        .limit(1);
      const maxN = data?.[0]?.locno ? parseInt(String(data[0].locno).slice(1), 10) || 0 : 0;
      const locno = "P" + String(maxN + 1).padStart(4, "0");
      const { error } = await supabase.from("pegasus_location").insert({
        custno: v.custno, locno,
        locname: v.locname.trim() || null, add1: v.add1.trim() || null,
        city: v.city.trim() || null, state: v.state.trim() || "MN", zip: v.zip.trim() || null,
        notes: v.notes?.trim() || null, created_by: who,
      });
      if (error) throw error;
      return locno;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["customer"] }),
  });
}

/** Move a PEGASUS-ADDED property to a different customer. ESC properties
 *  can't move until cutover (their home is ESC's own tables). */
export function useMoveProperty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { fromCustno: string; locno: string; toCustno: string }) => {
      // Renumber into the target's P-sequence so it never collides there.
      const { data } = await supabase
        .from("pegasus_location")
        .select("locno")
        .eq("custno", v.toCustno)
        .like("locno", "P%")
        .order("locno", { ascending: false })
        .limit(1);
      const maxN = data?.[0]?.locno ? parseInt(String(data[0].locno).slice(1), 10) || 0 : 0;
      const newLocno = "P" + String(maxN + 1).padStart(4, "0");
      const { error } = await supabase
        .from("pegasus_location")
        .update({ custno: v.toCustno, locno: newLocno, moved_from_custno: v.fromCustno, updated_at: new Date().toISOString() })
        .eq("custno", v.fromCustno)
        .eq("locno", v.locno);
      if (error) throw error;
      return newLocno;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["customer"] }),
  });
}

export function useCustomer(custno: string | undefined) {
  return useQuery({
    queryKey: ["customer", custno],
    queryFn: async (): Promise<CustomerWithLocations | null> => {
      if (!custno) return null;

      const [{ data: customer, error: cErr }, { data: locs, error: lErr }, { data: pegLocs }, { data: ovr }] =
        await Promise.all([
          supabase
            .from("mirror_customer")
            .select("*")
            .eq("custno", custno)
            .maybeSingle(),
          supabase.from("mirror_location").select("*").eq("custno", custno),
          // PROP-01: Pegasus-added properties ride the same bar.
          supabase.from("pegasus_location").select("*").eq("custno", custno).eq("active", true).order("locno"),
          // RECORD-READ-MODEL (34i97): the office correction rides on the record itself.
          supabase.from("pegasus_customer_override").select("display_name, customer_type").eq("custno", custno).maybeSingle(),
        ]);
      if (cErr) throw cErr;
      if (lErr) throw lErr;
      const overlay = ((pegLocs ?? []) as PegLocationRow[]).map(pegLocToRow);

      // Pegasus-native customers (NEWCUST) aren't in the ESC mirror — fall back.
      if (!customer) {
        const { data: pc } = await supabase
          .from("pegasus_customer")
          .select("*")
          .eq("custno", custno)
          .maybeSingle();
        if (!pc) return null;
        const base = pegasusToCustomer(pc as PegasusCustomerRow);
        return { ...base, locations: [...base.locations, ...overlay] };
      }

      const o = (ovr ?? null) as { display_name: string | null; customer_type: "residential" | "commercial" | null } | null;
      const row = customer as CustomerRow;
      return {
        ...row,
        native: false,
        esc_fullname: row.fullname,
        // The corrected name IS the name — the tab title, the picker, the statement, Merge, Book a Call all read it here.
        fullname: o?.display_name?.trim() ? o.display_name.trim() : row.fullname,
        customer_type: o?.customer_type ?? null,
        locations: [...((locs ?? []) as LocationRow[]), ...overlay],
      };
    },
    enabled: !!custno,
    staleTime: 60 * 1000,
  });
}

/**
 * Get the customer's COMPLETE dispatch history, all-time, newest first.
 *
 * Uses v_customer_dispatch_history (NOT v_route_data). v_route_data is the
 * dispatch-board view — it's filtered to service dept ('03') with an assigned
 * tech, so it silently dropped install / plumbing / agreement / unassigned
 * dispatches from a customer's history (e.g. Moller showed 29 service calls
 * while the lifetime metric counts all 34). The history view returns one row
 * per dispatch across ALL departments, so the count matches the prose summary.
 * The install/plumbing rows carry their INSTAL/PLUMB window badge so the
 * dispatcher can see what kind of call each was. Limited to 100 for now.
 */
export function useCustomerDispatches(custno: string | undefined) {
  return useQuery({
    queryKey: ["customer_dispatches", custno],
    queryFn: async () => {
      if (!custno) return [];
      // ESC history + PEGASUS-NATIVE visits in ONE stream (Seth 2026-08-28:
      // "recent service isn't showing pegasus history") — scheduled Pegasus
      // bookings also feed the strip's "next visit" fact.
      const [{ data, error }, { data: peg }] = await Promise.all([
        supabase
          .from("v_customer_dispatch_history")
          .select(
            "dispatch, tech_empno, appointment_date, tpromtime, window_code, status, notes, custno, locno, locname, service_address, city, tech_name, tech_first, tech_last",
          )
          .eq("custno", custno)
          .order("appointment_date", { ascending: false, nullsFirst: false })
          .limit(100),
        supabase
          .from("pegasus_dispatch")
          .select("id, dispatch_no, status, scheduled_date, summary, notes, locno, address, city, assigned_empno, service_type_name")
          .eq("custno", custno)
          .is("deleted_at", null)
          .in("status", ["scheduled", "completed"])
          .not("scheduled_date", "is", null)
          .order("scheduled_date", { ascending: false })
          .limit(50),
      ]);
      if (error) throw error;
      const pegRows = (peg ?? []) as { id: string; dispatch_no: number | null; status: string | null; scheduled_date: string; summary: string | null; notes: string | null; locno: string | null; address: string | null; city: string | null; assigned_empno: string | null; service_type_name: string | null }[];
      const empnos = [...new Set(pegRows.map((r) => r.assigned_empno).filter(Boolean))] as string[];
      const nameBy = new Map<string, string>();
      if (empnos.length) {
        const { data: emps } = await supabase
          .from("mirror_employee")
          .select("empno, firstname, lastname, empname")
          .in("empno", empnos);
        for (const e of (emps ?? []) as { empno: string; firstname: string | null; lastname: string | null; empname: string | null }[]) {
          nameBy.set(e.empno, [e.firstname, e.lastname].filter(Boolean).join(" ").trim() || e.empname || e.empno);
        }
      }
      const mapped: CustomerDispatchRow[] = pegRows.map((r) => ({
        dispatch: r.dispatch_no != null ? String(r.dispatch_no) : `peg:${r.id}`,
        tech_empno: r.assigned_empno ?? "",
        appointment_date: r.scheduled_date,
        tpromtime: null,
        window_code: null,
        status: r.status === "completed" ? "Complete" : "Scheduled",
        notes: [r.service_type_name, r.summary].filter(Boolean).join(" — ") || r.notes,
        custno,
        locno: r.locno,
        locname: null,
        service_address: r.address,
        city: r.city,
        tech_name: r.assigned_empno ? nameBy.get(r.assigned_empno) ?? null : null,
        tech_first: null,
        tech_last: null,
        peg: true,
      }));
      return [...((data ?? []) as CustomerDispatchRow[]), ...mapped]
        .sort((a2, b2) => (b2.appointment_date ?? "").localeCompare(a2.appointment_date ?? ""));
    },
    enabled: !!custno,
    staleTime: 60 * 1000,
  });
}

// CUST-03 / BUILD-17: accounts that share this service address (prior owner /
// builder / tenant), with each one's service tenure AT THIS ADDRESS. Derived
// from address match across custnos — surfaces history, never asserts
// "previous owner". Backed by property_history_at().
export interface PropertyHistoryRow {
  custno: string;
  locno: string | null;
  fullname: string | null;
  locname: string | null;
  first_service: string | null;
  last_service: string | null;
  visit_count: number;
  is_self: boolean;
}
export function usePropertyHistory(
  custno: string | undefined,
  locno: string | null | undefined,
) {
  return useQuery({
    queryKey: ["property_history", custno, locno],
    enabled: !!custno,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<PropertyHistoryRow[]> => {
      const { data, error } = await supabase.rpc("property_history_at", {
        p_custno: custno as string,
        p_locno: locno ?? null,
      });
      if (error) throw error;
      return (data ?? []) as PropertyHistoryRow[];
    },
  });
}

// BUILD-17 (DEC-26): when a notes-found prior owner carries a handoff_date,
// split THIS account's dispatch history at that boundary so the current owner's
// visit count reflects only their era — not the prior owner's service the reused
// ESC account dragged along. Returns null when there's no dated handoff.
export interface OwnerVisitSplit {
  prior_owner: string;
  handoff_date: string;
  prior_visits: number;
  current_visits: number;
  total_visits: number;
}
export function useOwnerVisitSplit(
  custno: string | undefined,
  locno: string | null | undefined,
) {
  return useQuery({
    queryKey: ["owner_visit_split", custno, locno],
    enabled: !!custno,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<OwnerVisitSplit | null> => {
      const { data, error } = await supabase.rpc("owner_visit_split", {
        p_custno: custno as string,
        p_locno: locno ?? null,
      });
      if (error) throw error;
      const rows = (data ?? []) as OwnerVisitSplit[];
      return rows.length > 0 ? rows[0] : null;
    },
  });
}

// MIG-NOTES-01: structured signals extracted from this customer's notes
// (gate/lockbox codes, access, heads-up, prefs, filters, hazards). Confirmed
// ones are operational truth; proposed ones await a one-click confirm/dismiss.
export interface CustomerSignal {
  id: string;
  custno: string;
  locno: string | null;
  signal_type: string;
  label: string | null;
  value: string;
  detail: Record<string, unknown> | null;
  source_text: string | null;
  confidence: number | null;
  status: "proposed" | "confirmed" | "dismissed";
  /** 'notes_ai' (the extractor) or 'manual' (typed by the office). */
  source: string | null;
  confirmed_by: string | null;
  confirmed_at: string | null;
  created_at: string | null;
  /** 360-B: switched off in the Watch-outs editor — on file, not on the record. */
  hidden_at: string | null;
  hidden_by: string | null;
  /** Set when an owner transfer archived this as the PRIOR owner's note. */
  archived_at: string | null;
  archived_for: string | null;
}
export function useCustomerSignals(custno: string | undefined) {
  return useQuery({
    queryKey: ["customer_signals", custno],
    enabled: !!custno,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<CustomerSignal[]> => {
      const { data, error } = await supabase
        .from("extracted_signal")
        .select("*")
        .eq("custno", custno as string)
        .in("status", ["confirmed", "proposed", "dismissed"])
        .order("status", { ascending: true })
        .order("signal_type", { ascending: true });
      if (error) throw error;
      return (data ?? []) as CustomerSignal[];
    },
  });
}
export function useResolveSignal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      id: string;
      status: "confirmed" | "dismissed" | "proposed";
    }) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("extracted_signal")
        .update({
          status: v.status,
          confirmed_by: v.status === "confirmed" ? (u?.user?.email ?? null) : null,
          confirmed_at: v.status === "confirmed" ? new Date().toISOString() : null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["customer_signals"] }),
  });
}

// Carry a PREVIOUS owner's access note forward to the current owner — for the
// physical-property facts that survive a sale (a gate code, a lockbox on the
// house). Un-archives it back into the active card as a PROPOSAL the office
// confirms (never a silent carry-over). Owner-preference notes stay archived.
export function useCarrySignal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("extracted_signal")
        .update({
          archived_at: null,
          archived_for: null,
          status: "proposed",
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["customer_signals"] }),
  });
}

// Edit a signal's value in place (from the good-to-know slide-out). A human
// edit = curated truth, so it also flips the signal to CONFIRMED + stamps who.
export function useUpdateSignalValue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; value: string; custno: string; signal_type?: string }) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("extracted_signal")
        .update({
          value: v.value.trim(),
          // SIGNALS-EDIT-01: the category is editable too (a "heads-up" that's
          // really a gate code moves to the right bucket).
          ...(v.signal_type ? { signal_type: v.signal_type } : {}),
          status: "confirmed",
          confirmed_by: u?.user?.email ?? null,
          confirmed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: ["customer_signals", v.custno] }),
  });
}

/** SIGNALS-EDIT-01 (Seth 2026-09-01: finish add/edit/remove) — a dispatcher
 *  adds an access note or preference BY HAND: born confirmed, stamped, source
 *  'manual' so it never reads as an AI guess. */
export function useAddSignal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { custno: string; locno?: string | null; signal_type: string; value: string; detail?: Record<string, unknown> | null }) => {
      const { data: u } = await supabase.auth.getUser();
      const now = new Date().toISOString();
      const { error } = await supabase.from("extracted_signal").insert({
        custno: v.custno,
        locno: v.locno ?? null,
        signal_type: v.signal_type,
        value: v.value.trim(),
        // SCHED-AVAIL-01: the scheduler stores its structured capture here so
        // "Use" on a later booking re-applies it exactly.
        ...(v.detail !== undefined ? { detail: v.detail } : {}),
        source: "manual",
        status: "confirmed",
        confidence: 1,
        confirmed_by: u?.user?.email ?? null,
        confirmed_at: now,
        created_at: now,
        updated_at: now,
      });
      if (error) throw error;
    },
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: ["customer_signals", v.custno] }),
  });
}

// NOTES-AI-02 — make the extraction self-running. The first time a customer's
// 360 is opened, if we've never extracted their notes (no done-marker), fire
// extract-notes in the background and refresh the signals + ownership when it
// lands. So the pills, nickname, and owner-split populate on their own as the
// book gets used — no manual backfill, and new ESC notes get picked up on the
// next open (clearing the marker re-triggers a pass). Fires at most once per
// customer per session.
export function useEnsureSignalsExtracted(
  custno: string | undefined,
  locno?: string | null,
) {
  const qc = useQueryClient();
  const fired = useRef<Set<string>>(new Set());

  // Per-(custno, locno) marker — extraction is LOCATION-scoped (NOTES-AI v8) so
  // a property manager's units don't share each other's access/getting-in pills.
  const { data: done } = useQuery({
    queryKey: ["note_extraction_marker", custno, locno ?? null],
    enabled: !!custno && !!locno,
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<boolean> => {
      const { data } = await supabase
        .from("note_extraction_done")
        .select("custno")
        .eq("custno", custno as string)
        .eq("locno", locno as string)
        .maybeSingle();
      return !!data;
    },
  });

  useEffect(() => {
    if (!custno || !locno || done !== false) return; // wait for a location + a "not done"
    const key = `${custno}|${locno}`;
    if (fired.current.has(key)) return;
    fired.current.add(key);
    supabase.functions
      .invoke("extract-notes", { body: { custno, locno } })
      .then(() => {
        qc.invalidateQueries({ queryKey: ["customer_signals", custno] });
        qc.invalidateQueries({ queryKey: ["note_extraction_marker", custno, locno] });
        qc.invalidateQueries({ queryKey: ["owner_visit_split", custno] });
        qc.invalidateQueries({ queryKey: ["property_history", custno] });
      })
      .catch(() => {});
  }, [custno, locno, done, qc]);
}

// ---------- STRIP-01: the customer's AR position, shared ----------
// One query, two consumers: the header's aging strip AND the Overview answer
// strip. Whole-account by design — AR follows the CUSTOMER (mirror_receivable
// has no locno). Same queryKey both places = one fetch.
export interface BalanceBuckets {
  buckets: { current: number; d30: number; d60: number; d90: number };
  total: number;
  mirrored: boolean;
}

export function useCustomerBalance(custno: string | undefined) {
  return useQuery({
    queryKey: ["customer_balance", custno],
    enabled: !!custno,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<BalanceBuckets> => {
      const [{ data: open, error }, { count, error: countErr }, { data: pegInv }, { data: pegPay }] = await Promise.all([
        supabase
          .from("mirror_receivable")
          .select("invoice, inv_date, inv_amt, paid")
          .eq("custno", custno as string)
          .is("paid_off", null)
          .limit(500),
        supabase
          .from("mirror_receivable")
          .select("invoice", { count: "exact", head: true })
          .limit(1),
        // PEGASUS AR (Seth 2026-08-28: "why isn't the 360 showing a balance?"):
        // a truck- or office-issued Pegasus invoice is owed money the moment
        // it's issued — it joins the account balance right here, ESC mirror or not.
        supabase
          .from("pegasus_job_invoice")
          .select("id, total, completed_date, created_at")
          .eq("custno", custno as string)
          .in("status", ["issued", "finalized"])
          .limit(500),
        supabase
          .from("pegasus_payment")
          .select("*, pegasus_payment_allocation(*), pegasus_payment_refund(*)")
          .eq("custno", custno as string),
      ]);
      if (error) throw error;
      if (countErr) throw countErr;
      const buckets = { current: 0, d30: 0, d60: 0, d90: 0 };
      let total = 0;
      const now = Date.now();
      for (const r of (open ?? []) as { inv_date: string | null; inv_amt: number | null; paid: number | null }[]) {
        const owed = Math.max(0, (r.inv_amt ?? 0) - (r.paid ?? 0));
        if (owed <= 0.005) continue;
        total += owed;
        const days = r.inv_date ? Math.floor((now - new Date(r.inv_date).getTime()) / 86400000) : 0;
        if (days <= 30) buckets.current += owed;
        else if (days <= 60) buckets.d30 += owed;
        else if (days <= 90) buckets.d60 += owed;
        else buckets.d90 += owed;
      }
      const paid = invoicePaidMap((pegPay ?? []) as PegasusPayment[]);
      for (const r of (pegInv ?? []) as { id: string; total: number | null; completed_date: string | null; created_at: string | null }[]) {
        const owed = Math.max(0, Number(r.total ?? 0) - (paid.get(r.id) ?? 0));
        if (owed <= 0.005) continue;
        total += owed;
        const iso = r.completed_date || r.created_at;
        const days = iso ? Math.floor((now - new Date(iso.length <= 10 ? iso + "T00:00:00" : iso).getTime()) / 86400000) : 0;
        if (days <= 30) buckets.current += owed;
        else if (days <= 60) buckets.d30 += owed;
        else if (days <= 90) buckets.d60 += owed;
        else buckets.d90 += owed;
      }
      return { buckets, total, mirrored: (count ?? 0) > 0 };
    },
  });
}

// ----------------------------------------------------------------------------
// PROP-LTV-01 (Seth 2026-10-01) — what each PROPERTY has billed, lifetime.
// Lives here (the leaf data module) so the header, the rail and the overview
// can all read it without import cycles. One RPC round-trip: mirror_sales
// grouped by locno server-side (some accounts carry 1,900+ invoice rows).
// HDR-TWEAKS-01 added firstDate so the header can say "this property since
// Nov 2025" instead of letting the account's 12-year line speak for a
// building we first visited last fall.
// ----------------------------------------------------------------------------
export interface LocationLifetime {
  total: number;
  invoices: number;
  firstYear: number | null;
  /** YYYY-MM-DD of the first invoice at this property. */
  firstDate: string | null;
}

export function useLocationLifetimes(custno: string | undefined) {
  return useQuery({
    queryKey: ["location-lifetimes", custno],
    enabled: !!custno,
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<Map<string, LocationLifetime>> => {
      const { data, error } = await supabase.rpc("pegasus_location_lifetime", { p_custno: custno });
      if (error) throw error;
      const m = new Map<string, LocationLifetime>();
      type Row = { locno: string | null; total: number | string | null; invoice_count: number | string | null; first_date: string | null };
      for (const r of (data ?? []) as Row[]) {
        if (!r.locno) continue;
        m.set(r.locno, {
          total: Number(r.total ?? 0),
          invoices: Number(r.invoice_count ?? 0),
          firstYear: r.first_date ? Number(r.first_date.slice(0, 4)) : null,
          firstDate: r.first_date ?? null,
        });
      }
      return m;
    },
  });
}

// ----------------------------------------------------------------------------
// SNAP-FIX-01 (Seth 2026-10-01: "whats the best way to correct or update or
// tweak any of that info? Can we type to the AI and give it some updated
// info?") — the snapshot CORRECTIONS box. One standing office note per
// property (custno+locno) in pegasus_snapshot_note; summarize-location v18
// reads it as ABSOLUTE AUTHORITY over anything the AI inferred from ESC
// notes/history, and its cache hash includes it — so saving a correction
// regenerates the snapshot on the very next view. The correction never
// appears verbatim in the snapshot (the prompt forbids mentioning the
// mechanism); it just quietly makes the summary right.
// ----------------------------------------------------------------------------
export interface SnapshotNote {
  custno: string;
  locno: string;
  body: string;
  set_by: string | null;
  set_at: string;
}

export function useSnapshotNote(custno: string | undefined, locno: string | null | undefined) {
  return useQuery({
    queryKey: ["snapshot_note", custno, locno],
    enabled: !!custno && !!locno,
    queryFn: async (): Promise<SnapshotNote | null> => {
      const { data, error } = await supabase
        .from("pegasus_snapshot_note")
        .select("custno, locno, body, set_by, set_at")
        .eq("custno", custno as string)
        .eq("locno", locno as string)
        .maybeSingle();
      if (error) throw error;
      return (data as SnapshotNote | null) ?? null;
    },
  });
}

/** Save (or clear, by saving empty) the property's standing correction, then
 *  invalidate the snapshot so it regenerates with the fix baked in. */
export function useSaveSnapshotNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { custno: string; locno: string; body: string }) => {
      const body = v.body.trim();
      if (!body) {
        const { error } = await supabase
          .from("pegasus_snapshot_note")
          .delete()
          .eq("custno", v.custno)
          .eq("locno", v.locno);
        if (error) throw error;
        return;
      }
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_snapshot_note").upsert({
        custno: v.custno,
        locno: v.locno,
        body,
        set_by: u?.user?.email ?? null,
        set_at: new Date().toISOString(),
      });
      if (error) throw error;
    },
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ["snapshot_note", v.custno, v.locno] });
      // The summarizer's source hash includes the correction — refetching the
      // summary regenerates it with the office's facts in charge.
      void qc.invalidateQueries({ queryKey: ["location_notes_summary", v.custno, v.locno] });
    },
  });
}
